import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import {
  useRive,
  useStateMachineInput,
  Layout,
  Fit,
  Alignment,
} from "rive-react";
import { AiOutlineMail } from "react-icons/ai";
import { FaFacebookF } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { RiLockPasswordLine } from "react-icons/ri";
import { auth } from "../../shared/firebase";
import { signInWithProvider } from "./SignInWithProvider";
import { convertErrorCodeToMessage } from "../../shared/utils";
import toast from "react-hot-toast";

const STATE_MACHINE_NAME = "Login Machine";

const SignIn = ({ setIsSignIn, isSignIn }) => {
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [inputLookMultiplier, setInputLookMultiplier] = useState(0);
  const [loginButtonText, setLoginButtonText] = useState("Sign In");
  const [authenticated, setAuthenticated] = useState(false);

  const { rive: riveInstance, RiveComponent } = useRive({
    src: "/animated-login.riv",
    stateMachines: STATE_MACHINE_NAME,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
  });

  const isCheckingInput = useStateMachineInput(
    riveInstance,
    STATE_MACHINE_NAME,
    "isChecking"
  );
  const numLookInput = useStateMachineInput(
    riveInstance,
    STATE_MACHINE_NAME,
    "numLook"
  );
  const trigSuccessInput = useStateMachineInput(
    riveInstance,
    STATE_MACHINE_NAME,
    "trigSuccess"
  );
  const trigFailInput = useStateMachineInput(
    riveInstance,
    STATE_MACHINE_NAME,
    "trigFail"
  );
  const isHandsUpInput = useStateMachineInput(
    riveInstance,
    STATE_MACHINE_NAME,
    "isHandsUp"
  );

  useEffect(() => {
    if (emailRef?.current && !inputLookMultiplier) {
      setInputLookMultiplier(emailRef.current.offsetWidth / 400);
    }
    // eslint-disable-next-line
  }, [emailRef]);

  const onEmailChange = (e) => {
    const newVal = e.target.value;
    if (!isCheckingInput.value) {
      isCheckingInput.value = true;
    }
    const numChars = newVal.length;
    numLookInput.value = numChars * inputLookMultiplier;
  };

  const onEmailFocus = () => {
    isCheckingInput.value = true;
    if (
      numLookInput.value !==
      emailRef.current.value.length * inputLookMultiplier
    ) {
      numLookInput.value = emailRef.current.value.length * inputLookMultiplier;
    }
  };

  const signInHandler = (e) => {
    e.preventDefault();

    const email = emailRef.current.value;
    const password = passwordRef.current.value;

    if (!email.trim() || !password.trim()) return;

    setIsLoading(true);
    setLoginButtonText("Checking...");
    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        trigSuccessInput.fire();
        setAuthenticated(true);
        setLoginButtonText("Sign In");
        setIsLoading(false);
      })
      .catch((error) => {
        const errorMessage = convertErrorCodeToMessage(error.code);
        trigFailInput.fire();
        toast(errorMessage, {
          icon: "😧",
        });
        setLoginButtonText("Sign In");
        setIsLoading(false);
      });
  };

  const handleGoogleSignIn = () => {
    signInWithProvider(new GoogleAuthProvider(), "google", setAuthenticated);
  };

  const handleFacebookSignIn = () => {
    signInWithProvider(
      new FacebookAuthProvider(),
      "facebook",
      setAuthenticated
    );
  };

  useEffect(() => {
    if (authenticated) {
      toast("You're signed in successfully!", {
        icon: "👏",
      });

      navigate("/");
    }
    //eslint-disable-next-line
  }, [authenticated]);

  return (
    <>
      {isLoading && (
        <div className="z-10 flex justify-center items-center h-screen relative">
          <div className="w-28 h-28 border-[10px] rounded-full border-primary border-t-transparent animate-spin"></div>
        </div>
      )}
      <div className="p-3 sm:p-6 w-full h-full md:max-w-[60vw] lg:max-w-[40vw] absolute top-1/2 -translate-y-1/2 right-0 md:bg-dark-darken">
        <div className="flex flex-col mb-5 font-semibold mx-auto">
          <div className="flex items-center mr-auto mb-8 sm:mb-4">
            <Link to="/" className="pt-3 mr-5 md:hidden">
              <LazyLoadImage
                alt="Logo"
                src="/logoicon.png"
                effect="opacity"
                className="w-12 h-12"
              />
            </Link>
            <div className="font-semibold">
              <div className="uppercase tracking-wider text-xl font-medium mb-1">
                Welcome to SHWETFLIX
              </div>
              <div className="text-primary leading-none text-3xl md:text-4xl">
                Sign In
              </div>
            </div>
          </div>

          <div className="flex gap-4 mb-4">
            <button
              className="h-12 w-12 rounded-full bg-white flex justify-center items-center brightness-90 hover:scale-110 hover:brightness-110 transition duration-300"
              onClick={handleGoogleSignIn}
            >
              <FcGoogle size={25} />
            </button>
            <button
              className="h-12 w-12 rounded-full bg-white flex justify-center items-center brightness-90 hover:scale-110 hover:brightness-110 transition duration-300 bg-[#1778F2]"
              onClick={handleFacebookSignIn}
            >
              <FaFacebookF size={25} color="white" />
            </button>
          </div>
          <p className="text-lg">or use your email account </p>
        </div>

        <div className="m-auto w-11/12 sm:w-7/12 pb-5 sm:pb-0">
          <RiveComponent className="h-[250px] w-full" />
        </div>
        <form onSubmit={signInHandler}>
          <div className="relative mb-4">
            <input
              ref={emailRef}
              name="email"
              type="email"
              placeholder="Email"
              className="w-full bg-dark-lighten px-5 py-4 pr-12 rounded-xl outline-none peer text-white"
              onFocus={onEmailFocus}
              onChange={onEmailChange}
              onBlur={() => (isCheckingInput.value = false)}
            />
            <label
              htmlFor="email"
              className={`absolute left-5 text-gray-lighten transition duration-500 pointer-events-none translate-y-[-50%] visible
              peer-placeholder-shown:opacity-0 peer-placeholder-shown:invisible peer-placeholder-shown:translate-y-[-10%] ease-in-out`}
            >
              Email
            </label>
            <AiOutlineMail
              size={25}
              className="absolute top-1/2 -translate-y-1/2 right-4"
            />
          </div>
          <div className="relative mb-8 sm:mb-6">
            <input
              ref={passwordRef}
              name="password"
              type="password"
              placeholder="Password"
              className="w-full bg-dark-lighten px-5 py-4 pr-12 rounded-xl outline-none peer text-white"
              onFocus={() => (isHandsUpInput.value = true)}
              onBlur={() => (isHandsUpInput.value = false)}
            />
            <label
              htmlFor="password"
              className={`absolute left-5 text-gray-lighten transition duration-500 pointer-events-none translate-y-[-50%] visible 
              peer-placeholder-shown:opacity-0 peer-placeholder-shown:invisible peer-placeholder-shown:translate-y-[-10%] ease-in-out`}
            >
              Password
            </label>
            <RiLockPasswordLine
              size={25}
              className="absolute top-1/2 -translate-y-1/2 right-4"
            />
          </div>
          <button
            className="flex justify-center items-center px-12 py-3 rounded-full text-lg text-white
            bg-gradient-to-br from-primary via-red-700 to-orange-600 uppercase tracking-wider absolute left-1/2 -translate-x-1/2 group hover:scale-105 transition duration-500"
          >
            {loginButtonText}
          </button>
        </form>

        <p className="text-xl flex gap-2 mt-32 sm:mt-24 justify-center">
          <span>Not a member?</span>
          <button
            type="submit"
            onClick={() => setIsSignIn(!isSignIn)}
            className="text-red-600 transition duration-300 hover:underline hover:underline-offset-2"
          >
            Sign Up
          </button>
        </p>
      </div>
    </>
  );
};

export default SignIn;
